import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { amount, humanizeWarning, maskPhone, percentFromBps, shortDate } from '../lib/format';
import { whatsappNumber } from '../lib/till';
import { methodLabel, type CompleteSaleResult } from '../lib/types';
import { renderReceiptText } from '@sp/core';
import { Icon } from './icons';
import { Button, Kbd, Modal } from './ui';
import { HtmlReceipt } from './HtmlReceipt';
import { tr, useT } from '../i18n';
import { desktopBridge } from '../lib/desktop';

/**
 * After the sale: "Payment received".
 *
 * The server renders the receipt text so the printed text and the on-screen text cannot
 * diverge; this dialog shows that text on a paper-shaped preview and prints ONLY that sheet
 * (see the @media print rules in index.css), at 58 mm or 80 mm. When the chosen paper is
 * narrower than the server's text, the same receipt document is laid out again at the paper's
 * column count, so a line never runs off the edge of a 58 mm roll.
 *
 * The next customer is the point of this screen, so the main action does both jobs: print and
 * clear the till (Enter). A customer on the bill can have the receipt on WhatsApp instead — it
 * opens a chat with the receipt typed out, and the cashier presses send; nothing leaves the till
 * without that. Reprint is audited, because duplicate receipts are how GST returns get
 * questioned, so it asks for a reason first. Every figure comes from integer paise.
 */

interface Props {
  result: CompleteSaleResult;
  onClose: () => void;
  /** Reused for the reprint flow from the returns screen or history, where "Next customer" makes no sense. */
  closeLabel?: string;
  /** Who the bill was for, captured before the till cleared, for the WhatsApp receipt. */
  customer?: { name: string; phone: string | null } | null;
  /** What the customer saved against MRP and in discounts. */
  savedPaise?: number;
  /**
   * "reprint" when F11 brings back a receipt that was already handed over: then the only way to
   * print is the audited reprint, never a silent second original.
   */
  mode?: 'sale' | 'reprint';
}

type Paper = '58' | '80';
const PAPER_KEY = 'sp.receipt.paper';
/** Characters per line of a thermal printer's standard font on each roll. */
const COLUMNS: Record<Paper, number> = { '58': 32, '80': 42 };

/** The server's text when it fits the paper; otherwise the same document re-laid at its width. */
function textForPaper(result: CompleteSaleResult, paper: Paper): string {
  const columns = COLUMNS[paper];
  const server = result.receipt;
  if (server && Math.max(...server.split('\n').map((line) => line.length)) <= columns) return server;
  if (result.receiptDocument) {
    return renderReceiptText(result.receiptDocument, {
      name: 'Browser print',
      paperWidth: paper === '58' ? 58 : 80,
      characterWidth: columns,
      autoCut: false,
      openCashDrawer: false,
      copies: 1,
      driver: 'windows',
    });
  }
  return server ?? fallbackReceipt(result);
}

function readPaper(): Paper {
  try {
    return localStorage.getItem(PAPER_KEY) === '58' ? '58' : '80';
  } catch {
    return '80';
  }
}

/** A plain-text receipt built from the sale result, used only when the server could not render one. */
function fallbackReceipt(result: CompleteSaleResult): string {
  const width = 32;
  const row = (left: string, right: string) => left + ' '.repeat(Math.max(1, width - left.length - right.length)) + right;
  const rule = '-'.repeat(width);
  const out = [`${tr('rcpt.invoice')} ${result.invoiceNumber}`, rule, row(tr('rcpt.total'), amount(result.grandTotalPaise))];
  if (result.roundOffPaise !== 0) {
    out.push(row(tr('rcpt.roundOff'), `${result.roundOffPaise > 0 ? '+' : '-'}${amount(Math.abs(result.roundOffPaise))}`));
  }
  for (const payment of result.payments) {
    out.push(row(methodLabel(payment.method), amount(payment.amountPaise)));
  }
  if (result.changePaise > 0) out.push(row(tr('rcpt.change'), amount(result.changePaise)));
  out.push(rule, tr('rcpt.thanks'));
  return out.join('\n');
}

export function ReceiptDialog({ result, onClose, closeLabel: closeLabelProp, customer = null, savedPaise = 0, mode = 'sale' }: Props) {
  const t = useT();
  const closeLabel = closeLabelProp ?? t('receipt.nextCustomer');
  const reprintOnly = mode === 'reprint';
  const [paper, setPaper] = useState<Paper>(readPaper);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reprinted, setReprinted] = useState(false);

  const text = textForPaper(result, paper);
  const whatsapp = whatsappNumber(customer?.phone);
  const paidBy = result.payments.map((payment) => methodLabel(payment.method)).join(' + ') || '—';

  function choosePaper(next: Paper) {
    setPaper(next);
    try {
      localStorage.setItem(PAPER_KEY, next);
    } catch {
      // storage blocked: the choice lasts for this dialog
    }
  }

  /** Prints the sheet, then clears the till once the print dialog is done with it. */
  function printAndNext() {
    // On the desktop till: straight to the receipt printer, no print dialog.
    const desktop = desktopBridge();
    if (desktop) {
      void desktop.print().then((result) => (result.ok ? onClose() : setError(t('receipt.printFailed', { error: result.error ?? t('receipt.printerError') }))));
      return;
    }
    const done = () => {
      window.removeEventListener('afterprint', done);
      onClose();
    };
    window.addEventListener('afterprint', done);
    window.print();
  }

  function sendWhatsApp() {
    if (!whatsapp) return;
    // WhatsApp shows ```text``` in a fixed-width font, so the receipt's columns survive.
    const thanks = customer?.name ? t('receipt.whatsappThanksName', { name: customer.name.split(' ')[0] ?? '' }) : t('receipt.whatsappThanks');
    const message = `${thanks}\n\`\`\`\n${text}\n\`\`\``;
    window.open(`https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
  }

  /** Records the reprint on the server (audited), then sends the sheet to the printer. */
  async function reprint() {
    setBusy(true);
    setError(null);
    try {
      await api.get('/api/pos/sales/' + result.saleId + '/receipt', {
        reprint: 'true',
        reason: reason.trim() || t('receipt.reasonDefault'),
      });
      setReprinted(true);
      setAsking(false);
      setReason('');
      window.setTimeout(() => window.print(), 50);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('receipt.reprintFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={reprintOnly ? t('receipt.lastReceipt') : t('receipt.paymentReceived')}
      subtitle={customer?.name ? t('receipt.for', { name: customer.name }) : undefined}
      onClose={onClose}
      size="max-w-4xl"
      testId="receipt-dialog"
      hideClose
      restoreFocus="barcode"
      footer={
        <div className="no-print flex flex-wrap items-center justify-between gap-2">
          <Button
            variant={reprintOnly ? 'primary' : 'default'}
            data-testid="receipt-reprint"
            data-autofocus={reprintOnly ? '' : undefined}
            disabled={busy}
            onClick={() => setAsking((open) => !open)}
            aria-expanded={asking}
          >
            {t('receipt.reprint')}
          </Button>
          <Button variant="ghost" data-testid="receipt-close" onClick={onClose}>
            {reprintOnly ? t('receipt.close') : t('receipt.noPrint', { label: closeLabel })}
            <Kbd>Esc</Kbd>
          </Button>
        </div>
      }
    >
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="no-print min-w-0">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
              style={{ background: 'var(--ui-success-soft)', color: 'var(--ui-success)' }}
            >
              <Icon name="check" size={26} strokeWidth={2.6} />
            </span>
            <p className="tabular text-sm font-semibold text-slate-600" data-testid="receipt-invoice-number">
              {t('receipt.invoice', { number: result.invoiceNumber })}
            </p>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[11px] font-bold tracking-wide text-slate-500 uppercase">{t('receipt.total')}</p>
              <p className="tabular text-2xl font-extrabold text-slate-900">₹{amount(result.grandTotalPaise)}</p>
            </div>
            <div
              className="rounded-xl border px-4 py-3"
              style={
                result.changePaise > 0
                  ? { borderColor: 'var(--ui-success-solid)', background: 'var(--ui-success-soft)', color: 'var(--ui-success)' }
                  : { borderColor: 'var(--ui-border)', background: 'var(--ui-surface-2)' }
              }
            >
              <p className="text-[11px] font-bold tracking-wide uppercase">{result.changePaise > 0 ? t('receipt.change') : t('receipt.paidBy')}</p>
              <p className="tabular truncate text-2xl font-extrabold">{result.changePaise > 0 ? `₹${amount(result.changePaise)}` : paidBy}</p>
            </div>
            {savedPaise > 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[11px] font-bold tracking-wide text-slate-500 uppercase">{t('receipt.saved')}</p>
                <p className="tabular text-2xl font-extrabold" style={{ color: 'var(--ui-success)' }}>
                  ₹{amount(savedPaise)}
                </p>
              </div>
            ) : null}
          </div>

          <dl className="mt-3 space-y-1 text-sm">
            {result.roundOffPaise !== 0 ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">{t('cart.roundOff')}</dt>
                <dd className="tabular text-slate-700">
                  {result.roundOffPaise > 0 ? '+' : '−'}₹{amount(Math.abs(result.roundOffPaise))}
                </dd>
              </div>
            ) : null}
            {result.payments.map((payment, index) => (
              <div key={`${payment.method}-${index}`} className="flex justify-between gap-3">
                <dt className="text-slate-500">
                  {methodLabel(payment.method)}
                  {payment.reference ? ` · ${payment.reference}` : ''}
                </dt>
                <dd className="tabular text-slate-700">₹{amount(payment.amountPaise)}</dd>
              </div>
            ))}
          </dl>

          {result.warnings.length > 0 ? (
            <ul className="banner banner-warn mt-3 flex-col gap-1">
              {result.warnings.map((warning) => (
                <li key={warning}>{humanizeWarning(warning)}</li>
              ))}
            </ul>
          ) : null}

          {reprintOnly ? null : (
            <div className="mt-5 space-y-2">
              <Button
                variant="success"
                size="lg"
                className="min-h-14 w-full rounded-xl text-lg"
                data-testid="receipt-print-next"
                data-autofocus=""
                onClick={printAndNext}
              >
                <Icon name="printer" size={20} />
                {t('receipt.printNext')}
                <Kbd>Enter</Kbd>
              </Button>
              {whatsapp ? (
                <Button className="w-full" data-testid="receipt-whatsapp" onClick={sendWhatsApp}>
                  <Icon name="message" size={17} />
                  {t('receipt.whatsapp', { phone: maskPhone(customer?.phone ?? '') })}
                </Button>
              ) : null}
            </div>
          )}

          {asking ? (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <label htmlFor="reprint-reason" className="field-label">
                {t('receipt.reasonLabel')}
              </label>
              <div className="flex gap-2">
                <input
                  id="reprint-reason"
                  className="field"
                  value={reason}
                  placeholder={t('receipt.reasonDefault')}
                  data-autofocus=""
                  data-testid="receipt-reprint-reason"
                  onChange={(event) => setReason(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void reprint();
                    }
                  }}
                />
                <Button variant="primary" busy={busy} onClick={() => void reprint()} data-testid="receipt-reprint-confirm">
                  {t('receipt.printCopy')}
                </Button>
              </div>
            </div>
          ) : null}
          {reprinted ? (
            <p className="mt-2 text-xs" style={{ color: 'var(--ui-success)' }} role="status">
              {t('receipt.reprintRecorded')}
            </p>
          ) : null}
          {error ? (
            <p className="field-error mt-2" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <div className="min-w-0">
          <div className="no-print mb-2 flex items-center justify-between gap-2">
            <p className="text-[11px] font-bold tracking-wide text-slate-500 uppercase" id="receipt-preview-label">
              {t('receipt.preview')}
            </p>
            <div role="group" aria-label={t('receipt.paperWidth')} className="flex gap-1.5">
              {(['58', '80'] as const).map((width) => (
                <button key={width} type="button" className="chip min-h-8 px-3 text-xs" aria-pressed={paper === width} onClick={() => choosePaper(width)}>
                  {t('receipt.mm', { width })}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-[26rem] overflow-auto rounded-lg border border-slate-300 p-2" style={{ background: '#fff' }}>
            {result.receiptDocument?.language ? (
              <HtmlReceipt document={result.receiptDocument} paper={paper} />
            ) : (
            <pre
              className="receipt-sheet mx-auto text-xs text-black"
              style={{ width: paper === '58' ? '58mm' : '80mm', maxWidth: 'none', color: '#000' }}
              data-paper={paper}
              data-testid="receipt-text"
              aria-labelledby="receipt-preview-label"
            >
              {text}
            </pre>
            )}
          </div>
          {!result.receipt ? (
            <p className="no-print mt-2 text-xs text-slate-500">{t('receipt.summaryOnly')}</p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

export { maskPhone, percentFromBps, shortDate };
